import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { RichTextEditor } from '@/components/RichTextEditor';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Newspaper, BookOpen, Plus, Pencil, Trash2, ArrowLeft, Loader2, ImagePlus, X, Globe } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { title: '', excerpt: '', body: '', featured_image: null, status: 'draft' };

const TAB_META = {
  news: { label: 'Global News', newLabel: 'New Global News', icon: Newspaper,
    empty: 'No global news yet', emptyHint: 'Publish workforce updates that appear for every region.' },
  resource: { label: 'Employer Guides & Resources', newLabel: 'New Employer Guide', icon: BookOpen,
    empty: 'No employer guides yet', emptyHint: 'Create how-to guides that appear in every region\u2019s Resources.' },
};

export default function PlatformContent() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState('news');
  const [items, setItems] = useState({ news: [], resource: [] });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const navigate = useNavigate();

  const loadType = useCallback(async (type) => {
    const { data } = await axios.get(`${BACKEND_URL}/api/admin/platform-content?type=${type}`, { withCredentials: true });
    setItems((prev) => ({ ...prev, [type]: data || [] }));
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const u = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        setUser(u.data);
        await Promise.all([loadType('news'), loadType('resource')]);
      } catch (e) { toast.error('Failed to load content'); } finally { setLoading(false); }
    })();
  }, [loadType]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({ title: a.title, excerpt: a.excerpt || '', body: a.body || '', featured_image: a.featured_image || null, status: a.status });
    setOpen(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/platform-content/upload-image`, fd, { withCredentials: true });
      setForm((f) => ({ ...f, featured_image: data.url }));
      toast.success('Image uploaded');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const save = async (status) => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    const payload = { ...form, status: status || form.status, content_type: tab };
    try {
      if (editing) await axios.put(`${BACKEND_URL}/api/admin/platform-content/${editing.id}`, payload, { withCredentials: true });
      else await axios.post(`${BACKEND_URL}/api/admin/platform-content`, payload, { withCredentials: true });
      toast.success(payload.status === 'published' ? 'Published' : 'Draft saved');
      setOpen(false); await loadType(tab);
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    try { await axios.delete(`${BACKEND_URL}/api/admin/platform-content/${a.id}`, { withCredentials: true }); toast.success('Deleted'); await loadType(tab); }
    catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const meta = TAB_META[tab];
  const list = items[tab];

  return (
    <Layout user={user}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate('/dashboard')} data-testid="pc-back"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand mb-4 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Admin
        </button>
        <div className="flex items-center gap-3 mb-6">
          <span className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center flex-shrink-0"><Globe className="w-5 h-5 text-white" /></span>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 leading-tight">Content</h1>
            <p className="text-sm text-slate-500">Global content shown to employers across every region</p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
            <TabsList className="bg-slate-100 rounded-lg p-1">
              <TabsTrigger value="news" data-testid="pc-tab-news" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">Global News</TabsTrigger>
              <TabsTrigger value="resource" data-testid="pc-tab-resource" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">Employer Guides &amp; Resources</TabsTrigger>
            </TabsList>
            <Button onClick={openNew} data-testid="pc-new" className="bg-brand hover:bg-brand-hover text-white"><Plus className="w-4 h-4 mr-1.5" /> {meta.newLabel}</Button>
          </div>

          {['news', 'resource'].map((t) => (
            <TabsContent key={t} value={t}>
              {loading ? (
                <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand" /></div>
              ) : items[t].length === 0 ? (
                <Card className="p-12 text-center border border-slate-200" data-testid={`pc-empty-${t}`}>
                  <p className="text-slate-700 font-medium">{TAB_META[t].empty}</p>
                  <p className="text-sm text-slate-400 mt-1">{TAB_META[t].emptyHint}</p>
                </Card>
              ) : (
                <div className="space-y-3" data-testid={`pc-list-${t}`}>
                  {items[t].map((a) => (
                    <div key={a.id} data-testid={`pc-row-${a.id}`} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
                      {a.featured_image
                        ? <img src={a.featured_image} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                        : <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">{t === 'news' ? <Newspaper className="w-6 h-6 text-slate-300" /> : <BookOpen className="w-6 h-6 text-slate-300" />}</div>}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-900 truncate">{a.title}</h3>
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${a.status === 'published' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>{a.status}</span>
                        </div>
                        {a.excerpt && <p className="text-sm text-slate-500 truncate mt-0.5">{a.excerpt}</p>}
                        {a.published_at && a.status === 'published' && <p className="text-xs text-slate-400 mt-0.5">Published {new Date(a.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>}
                      </div>
                      <div className="flex gap-1.5 flex-shrink-0">
                        <Button onClick={() => openEdit(a)} data-testid={`pc-edit-${a.id}`} variant="outline" size="sm" className="border-slate-200"><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button onClick={() => remove(a)} data-testid={`pc-delete-${a.id}`} variant="outline" size="sm" className="border-slate-200 text-red-600 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pc-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : meta.newLabel}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div><Label className="text-sm">Title</Label>
              <Input data-testid="pc-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" placeholder="Title" /></div>
            <div><Label className="text-sm">Short excerpt</Label>
              <Textarea data-testid="pc-excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="mt-1 min-h-[60px]" placeholder="One or two sentences shown on cards" /></div>
            <div><Label className="text-sm">Body</Label>
              <div className="mt-1">
                <RichTextEditor full testId="pc-body" value={form.body}
                  onChange={(html) => setForm({ ...form, body: html })}
                  placeholder="Full article text. Use the toolbar for headings, lists, and links." />
              </div></div>
            <div>
              <Label className="text-sm">Featured image</Label>
              <div className="mt-1 flex items-center gap-3">
                {form.featured_image && (
                  <div className="relative">
                    <img src={form.featured_image} alt="" className="w-24 h-16 rounded-lg object-cover border border-slate-200" />
                    <button type="button" onClick={() => setForm({ ...form, featured_image: null })} aria-label="Remove image"
                      className="absolute -top-2 -right-2 bg-white rounded-full border border-slate-200 p-0.5 text-slate-500 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
                  </div>
                )}
                <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="pc-image-input" />
                <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()} className="border-slate-200" data-testid="pc-image-btn">
                  {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ImagePlus className="w-4 h-4 mr-1.5" />}
                  {form.featured_image ? 'Replace' : 'Upload image'}
                </Button>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={() => save('draft')} disabled={saving} className="border-slate-300" data-testid="pc-save-draft">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Save draft
            </Button>
            <Button onClick={() => save('published')} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid="pc-publish">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Publish
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
